import type {
  HttpApiEndpoint as HttpApiEndpointNs,
  HttpApiMiddleware,
} from "@effect/platform";
import { HttpApiEndpoint } from "@effect/platform";
import type { Context } from "effect";
import { HookdeckVerification } from "./hookdeck-middleware";
import { Authorization } from "./middleware";
import { PostmarkVerification } from "./postmark-middleware";
import { SystemAuthorization } from "./system-middleware";
import { TelnyxVerification } from "./telnyx-middleware";
import { WidgetAuthenticated, WidgetPublic } from "./widget-middleware";

type PathSegment = HttpApiEndpointNs.PathSegment;

/**
 * Creates endpoint constructors that pre-apply the given middleware.
 * Each method mirrors HttpApiEndpoint.get/post/put/patch/del but
 * calls .middleware() automatically.
 *
 * The generic constraint matches `.middleware()`'s own signature so
 * TypeScript preserves the concrete middleware identity type (`I`)
 * through the wrapper.
 */
function withMiddleware<I extends HttpApiMiddleware.HttpApiMiddleware.AnyId, S>(
  middleware: Context.Tag<I, S>,
) {
  return {
    get: <const N extends string>(name: N, path: PathSegment) =>
      HttpApiEndpoint.get(name, path).middleware(middleware),
    post: <const N extends string>(name: N, path: PathSegment) =>
      HttpApiEndpoint.post(name, path).middleware(middleware),
    put: <const N extends string>(name: N, path: PathSegment) =>
      HttpApiEndpoint.put(name, path).middleware(middleware),
    patch: <const N extends string>(name: N, path: PathSegment) =>
      HttpApiEndpoint.patch(name, path).middleware(middleware),
    del: <const N extends string>(name: N, path: PathSegment) =>
      HttpApiEndpoint.del(name, path).middleware(middleware),
  };
}

/**
 * Endpoint constructors with enforced auth selection.
 *
 * Every effect-api endpoint must use one of these instead of raw
 * `HttpApiEndpoint.get/post/...`. This prevents accidentally creating
 * endpoints without authentication.
 *
 * @example
 * ```ts
 * // Merchant app endpoint (requires bearer JWT)
 * endpoint.merchant.get("getSupportTicket", "/support-tickets/:id")
 *
 * // Widget public endpoint (requires x-widget-id header)
 * endpoint.widgetPublic.get("getWidgetConversation", "/widget/conversations/:id")
 *
 * // Explicitly unauthenticated (health checks, webhook callbacks)
 * endpoint.public.get("getHealth", "/health")
 * ```
 */
export const endpoint = {
  /** Merchant app auth — bearer JWT resolving to CurrentUser (team/user/permissions). */
  merchant: withMiddleware(Authorization),

  /** Internal service auth — bearer system JWT; the payload names the account and actor. */
  system: withMiddleware(SystemAuthorization),

  /** Hookdeck webhook verification — HMAC-SHA256 signature check. */
  webhook: withMiddleware(HookdeckVerification),

  /** Telnyx webhook verification — ED25519 signature check. */
  telnyxWebhook: withMiddleware(TelnyxVerification),

  /** Postmark inbound webhook auth observation — logs invalid shared-secret Basic auth. */
  postmarkWebhook: withMiddleware(PostmarkVerification),

  /** Widget public — resolves team from x-widget-id header. No customer auth. */
  widgetPublic: withMiddleware(WidgetPublic),

  /** Widget authenticated — requires x-widget-id header + customer bearer JWT. */
  widgetAuth: withMiddleware(WidgetAuthenticated),

  /** No auth. Use sparingly — health checks, external validation callbacks only. */
  public: {
    get: HttpApiEndpoint.get,
    post: HttpApiEndpoint.post,
    put: HttpApiEndpoint.put,
    patch: HttpApiEndpoint.patch,
    del: HttpApiEndpoint.del,
  },
};
